import Block from "Components/UI/Block/Block";
import classNames from "classnames";
import React, { useEffect } from "react";
import styles from "./RecentComments.module.scss";
import {
  selectDashboardRecentComments,
  selectDashboardRecentCommentsLoading,
} from "Redux/Dashboard/slice";
import { useSelector } from "react-redux";
import { useAppDispatch } from "Redux/store";
import { getDashboardRecentComments } from "Redux/Dashboard/reducer";
import Link from "Components/UI/Link/Link";
import {
  GET_UNIT_DETAIL_PATH,
  USER,
  WORK_ORDERS,
  PART,
  ISSUE,
  FUEL_HISTORY,
  REMINDERS_SERVICES,
  VENDOR,
} from "Shared/Constants/routes";
import { SkeletonStatus, SkeletonText } from "Components/Dashboard/Skeleton";
import { formatPersonName } from "Shared/Utils/personName";
import { displayDate } from "Shared/Utils/display";

// Enough items to reach the card's max height, as a full list does.
const SKELETON_ITEMS = 6;

const getRedirectPath = (objectType: string, objectId: string) => {
  switch (objectType) {
    case "contact":
      return `${USER}/${objectId}`;
    case "issue":
      return `${ISSUE}/${objectId}`;
    case "work_order":
      return `${WORK_ORDERS}/${objectId}`;
    case "part":
      return `${PART}/${objectId}`;
    case "service_reminder":
      return `${REMINDERS_SERVICES}/${objectId}`;
    case "fuel_entry":
      return `${FUEL_HISTORY}/${objectId}`;
    case "vehicle":
      return GET_UNIT_DETAIL_PATH(objectId);
    case "vendor":
      return `${VENDOR}/${objectId}`;
    default:
      return undefined;
  }
};

const RecentComments: React.FC = () => {
  const dispatch = useAppDispatch();
  const recent = useSelector(selectDashboardRecentComments);
  const loading = useSelector(selectDashboardRecentCommentsLoading);

  useEffect(() => {
    dispatch(getDashboardRecentComments());
  }, [dispatch]);

  return (
    <Block
      className={classNames(styles.block, loading && styles.block_loading)}
    >
      <h3>Recent Comments</h3>
      <div>
        {loading ? (
          <>
            <SkeletonStatus>Loading recent comments…</SkeletonStatus>
            <div aria-hidden="true">
              {Array.from({ length: SKELETON_ITEMS }, (_, index) => (
                <div key={index} className={styles.comments__item}>
                  <div className={styles.comments__item__container}>
                    <div className={styles.comments__item__container__avatar} />
                    <div className={styles.comments__item__container__text}>
                      <SkeletonText width="16em" />
                    </div>
                  </div>
                  <div className={styles.comments__item__time}>
                    <SkeletonText width="6em" />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : recent.length > 0 ? (
          recent.map((el) => (
            <div key={el.id} className={styles.comments__item}>
              <div className={styles.comments__item__container}>
                <div className={styles.comments__item__container__avatar} />
                <div className={styles.comments__item__container__text}>
                  <span>{formatPersonName(el.created_by, "Unknown user")}</span> commented on{" "}
                  <span>
                    {el.object_type.replace(/_/g, " ")}{" "}
                    {getRedirectPath(el.object_type, el.object_id) ? (
                      <Link
                        to={getRedirectPath(el.object_type, el.object_id) ?? ""}
                      >
                        ({el.object_name})
                      </Link>
                    ) : (
                      <span>({el.object_name})</span>
                    )}
                  </span>
                </div>
              </div>
              <div className={styles.comments__item__time}>
                {displayDate(el.created)}
              </div>
            </div>
          ))
        ) : (
          <h4>No recent comments</h4>
        )}
      </div>
    </Block>
  );
};

export default RecentComments;
